import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { User } from "@/api/client/models/index.ts";
import { isFeatureEnabled } from "@/config/feature-flags.ts";

export const APP_IDS = ["allotment", "charter"] as const;
export type AppId = (typeof APP_IDS)[number];

const DEFAULT_APP_ID: AppId = "allotment";
const CURRENT_APP_STORAGE_KEY = "currentApp";

function isAppId(value: unknown): value is AppId {
  return typeof value === "string" && APP_IDS.includes(value as AppId);
}

function isEnabledAppId(value: AppId): boolean {
  return value !== "charter" || isFeatureEnabled("charterApp");
}

function getInitialCurrentApp(): AppId {
  if (typeof window === "undefined") {
    return DEFAULT_APP_ID;
  }

  try {
    const storedValue = localStorage.getItem(CURRENT_APP_STORAGE_KEY);
    return isAppId(storedValue) && isEnabledAppId(storedValue) ? storedValue : DEFAULT_APP_ID;
  } catch {
    return DEFAULT_APP_ID;
  }
}

export const useCurrentAppStore = defineStore("current-app", () => {
  const currentApp = ref<AppId>(getInitialCurrentApp());
  const initializedFromCurrentUser = ref(false);

  const isAllotmentApp = computed(() => currentApp.value === "allotment");
  const isCharterApp = computed(() => currentApp.value === "charter");

  function setCurrentApp(appId: AppId): void {
    const nextAppId = isEnabledAppId(appId) ? appId : DEFAULT_APP_ID;

    currentApp.value = nextAppId;
    initializedFromCurrentUser.value = true;

    if (typeof window === "undefined") {
      return;
    }

    try {
      localStorage.setItem(CURRENT_APP_STORAGE_KEY, nextAppId);
    } catch {
      // Ignore localStorage failures and keep in-memory state.
    }
  }

  function setInitialCurrentAppFromUser(currentUser: User | null | undefined): void {
    if (initializedFromCurrentUser.value) {
      return;
    }

    initializedFromCurrentUser.value = true;

    if (!isFeatureEnabled("charterApp") || !currentUser?.charterRoleId) {
      setCurrentApp(DEFAULT_APP_ID);
      return;
    }

    setCurrentApp(currentUser.isAllotmentDefaultView === false ? "charter" : "allotment");
  }

  return {
    currentApp,
    isAllotmentApp,
    isCharterApp,
    setCurrentApp,
    setInitialCurrentAppFromUser,
  };
});
